import type { ComponentProps } from "react";
import React from "react";
import { tv } from "tailwind-variants";
import type { VariantProps } from "tailwind-variants";

import { cn } from "@/lib/helpers/cn";

type BadgeProps = ComponentProps<"span"> & VariantProps<typeof badgeStyles>;

export function Badge2({ children, className, size, variant, ...badgeProps }: BadgeProps) {
  const { body } = badgeStyles({ size, variant });

  return (
    <span className={cn(body(), className)} role="status" {...badgeProps}>
      {children}
    </span>
  );
}

export const badgeStyles = tv({
  defaultVariants: {
    size: "sm",
    variant: "neutral",
  },
  slots: {
    body: cn(
      "tw:inline tw:h-auto",
      "trim-both tw:rounded-sm tw:text-center tw:uppercase tw:select-none",
      "tw:print:[print-color-adjust:exact]",
      "tw:my-auto",
    ),
  },
  variants: {
    size: {
      md: {
        body: "tw:px-1.5 tw:py-2 label-sm tw:font-bold",
      },
      sm: {
        body: "tw:px-1 tw:py-1 label-xs",
      },
      xs: {
        body: "tw:px-1 tw:py-1 label-xs tw:text-xs",
      },
    },
    variant: {
      error: {
        body: "tw:bg-fill-error-primary tw:text-text-white",
      },
      navy: {
        body: "tw:bg-fill-navy-primary tw:text-text-white",
      },
      neutral: {
        body: "tw:bg-fill-quinary tw:text-text-secondary ",
      },
      orange: {
        body: "tw:bg-fill-orange-primary tw:text-text-white",
      },
      "outline-error": {
        body: "tw:border tw:border-fill-error-primary tw:bg-transparent tw:text-fill-error-primary",
      },
      "outline-neutral": {
        body: "tw:border tw:border-fill-quaternary tw:bg-transparent tw:text-text-secondary",
      },
      "outline-success": {
        body: "tw:border tw:border-fill-success-primary tw:bg-transparent tw:text-fill-success-primary",
      },
      success: {
        body: "tw:bg-fill-success-primary tw:text-text-white",
      },
      warning: {
        body: "tw:bg-fill-warning-secondary tw:text-text-primary",
      },
    },
  },
});
